import { useTranslation } from '@wix/yoshi-flow-editor';
import { useStyles } from '@wix/tpa-settings/react';
import { FeaturedType } from 'root/clientTypes';
import { FeaturedBadgePlacement } from 'root/consts';
import { useIsFeaturedTypeEnabled } from 'root/hooks/useIsFeaturedTypeEnabled';
import { stylesParams } from 'root/components/Menus/stylesParams';
import { LayoutStylesParams } from 'root/components/Menus/Settings/Tabs/LayoutTab/LayoutStylesParams';
import { Preset } from 'root/utils/presets';
import { getFeaturedTypeTranslationKey, isSupportedFeaturedType } from 'root/utils/featuredType';

/**
 * Only the grid layout puts the item image somewhere a badge can sit on it, so that is the
 * one layout offering the on-image placement — in the panel and in the widget alike.
 */
export const canPlaceFeaturedBadgeOnImage = (styles: ReturnType<typeof useStyles>) =>
  styles.get(LayoutStylesParams.preset) === Preset.Grid && styles.get(stylesParams.showItemImage);

export const useFeaturedBadgeLogic = ({ featuredType }: { featuredType?: FeaturedType }) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const isFeaturedTypeEnabled = useIsFeaturedTypeEnabled();

  // Items highlighted before types existed (no type set) fall back to the generic
  // "Featured" label so they keep showing a badge when the FT is on.
  // Items with an unrecognised future type still show nothing.
  const effectiveFeaturedType = featuredType ?? FeaturedType.FEATURED;
  const label =
    isFeaturedTypeEnabled && isSupportedFeaturedType(effectiveFeaturedType)
      ? t(getFeaturedTypeTranslationKey(effectiveFeaturedType))
      : undefined;

  return {
    label,
    isFeaturedTypeEnabled,
    // On-image is offered for the grid layout only, so every other layout keeps the badge
    // in the item info however the placement happens to be set.
    isOnImage:
      canPlaceFeaturedBadgeOnImage(styles) &&
      styles.get(stylesParams.featuredBadgePlacement) === FeaturedBadgePlacement.OnImage,
    isVisible: !!label && styles.get(stylesParams.showFeaturedBadge),
  };
};
